import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { useI18n } from '@/contexts/I18nContext';
import brFlag from '@/assets/images/flags/br.svg';
import enFlag from '@/assets/images/flags/en.svg';

interface LanguageSelectorProps {
  className?: string;
  variant?: 'default' | 'compact';
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  className = '', 
  variant = 'default' 
}) => {
  const { currentLanguage, changeLanguage, isLoading } = useI18n();
  const [open, setOpen] = React.useState(false);

  const handleLanguageChange = async (language: string) => {
    if (language === 'EN') {
      await changeLanguage('en');
    } else if (language === 'BR') {
      await changeLanguage('pt');
    }
    setOpen(false);
  };

  if (isLoading) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <div className="h-7 w-9 bg-gray-300 animate-pulse rounded" />
        <div className="w-8 h-4 bg-gray-300 animate-pulse rounded" />
      </div>
    );
  }

  const displayLanguage = currentLanguage === 'pt' ? 'BR' : 'EN';
  const flagSrc = currentLanguage === 'pt' ? brFlag : enFlag;
  const flagAlt = currentLanguage === 'pt' ? 'Português' : 'English';

  return (
    <Select 
      value={displayLanguage} 
      onValueChange={handleLanguageChange} 
      open={open} 
      onOpenChange={setOpen}
    >
      <SelectTrigger 
        className={`w-auto h-10 bg-transparent border-0 !border-none px-3 py-2 text-white focus:outline-none !ring-0 !outline-none relative [&_[data-radix-select-icon]]:hidden flex items-center ${className}`} 
        style={{border:'none',boxShadow:'none',outline:'none'}}
      >
        <div className="flex items-center gap-1">
          <img 
            src={flagSrc} 
            alt={flagAlt} 
            className="h-7 w-9 object-contain"
          />
          <span className="font-bold text-white">{displayLanguage}</span>
          {open ? (
            <ChevronUp className="w-5 h-5 text-white" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white" />
          )}
        </div>
      </SelectTrigger>
      <SelectContent className="bg-black/60 border-0 p-2">
        <SelectItem value="EN" className="text-white">
          <div className="flex items-center gap-2">
            <img 
              src={enFlag} 
              alt="English" 
              className="h-5 w-7 object-contain"
            />
            <span className="font-bold">EN</span>
          </div>
        </SelectItem>
        <SelectItem value="BR" className="text-white">
          <div className="flex items-center gap-2">
            <img 
              src={brFlag} 
              alt="Português" 
              className="h-5 w-7 object-contain"
            />
            <span className="font-bold">BR</span>
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  );
};




